import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import {
  extractRisks,
  FileOrderInsightsType,
  OrderInsightsError,
  Party,
} from 'api/file/interfaces/get-file-order-insights';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { getFileInfo, GetFileInfoProps, getFileOrderInsights } from '../api/file/file-api';
import { FeatureFlag } from '../utilities/constants/launch-darkly-flags';
import { fileInfoType } from '../api/file/interfaces/get-file';
import { DefaultNewFile } from '../models/file-model';
import { fileSchema } from '../utilities/validation/schemas';
import { doValidate, FieldValidationError } from '../utilities/validation/validation';
import { BaggageKeys, useTelemetry } from '../telemetry/useTelemetry';

export interface FileDataContextInterface {
  tabValue?: string;
  setTabValue?: (val: string) => void;
  activeTab?: string | null;
  setActiveTab?: (activeTab: string) => void;
  fileData?: fileInfoType | null;
  loadFileData?: (fileProps: GetFileInfoProps, callback?: () => void) => void;
  setFileData?: (data: fileInfoType | null | any) => void;
  setCleanData?: (data: fileInfoType | null | undefined) => void;
  cleanData?: fileInfoType | null | undefined;
  fileOrderInsightsData?: FileOrderInsightsType | null;
  setFileOrderInsightsData?: (data: FileOrderInsightsType | null) => void;
  loadFileOrderInsightsData?: (fileId: string, immediate?: boolean) => void;
  isDiscoveringOrderInsights?: string;
  children?: React.ReactNode;
  validationErrors?: FieldValidationError[];
  setValidationErrors?: (errs: FieldValidationError[]) => void;
  openSaveModal?: boolean;
  setOpenSaveModal?: (val: boolean) => void;
  isFormDirty?: boolean;
  setIsFormDirty?: (val: boolean) => void;
  setLogoutAttempted?: (val: boolean) => void;
  logoutAttempted?: boolean;
  setRatesAndFeeDrawerClicked?: (val: boolean) => void;
  isRatesAndFeeDrawerClicked?: boolean;
  setOpenRatesAndFeeDrawer?: (val: boolean) => void;
  openRatesAndFeeDrawer?: boolean;
  openCreateDrawer?: boolean;
  setOpenCreateDrawer?: (val: boolean) => void;
  isCreateDrawerClicked?: boolean;
  setIsCreateDrawerClicked?: (val: boolean) => void;
  openPendingCpl?: number;
  setOpenPendingCpl?: (val: number) => void;
  resetCpl?: boolean;
  setResetCpl?: (val: boolean) => void;
  hasRemittableCpl?: boolean;
  setRemmittableCpl?: (val?: boolean) => void;
  fileDataUpdated?: boolean;
  setfileDataUpdated?: (val: boolean) => void;
  partyUpdated?: boolean;
  setPartyUpdated?: (val: boolean) => void;
  firmAnchorEl?: any;
  setFirmAnchorEl?: any;
  officeDisabled?: boolean;
  setOfficeDisabled?: (val: boolean) => void;
  underwriterDisabled?: boolean;
  setUnderwriterDisabled?: (val: boolean) => void;
  resetFileNoDisable?: boolean;
  setResetFileNoDisable?: (val: boolean) => void;
  fileRefreshTrigger?: boolean;
  triggerFileRefresh?: (value: boolean) => void;
  sessionFileId?: string | null;
  setSessionFileId?: (id: string | null) => void;
}
const FileDataContext = React.createContext<FileDataContextInterface | null>(null);
const FileDataProvider = (props: FileDataContextInterface): JSX.Element => {
  const { getAccessToken } = useAuth();
  const { addSnackbarMessage } = useSnackBars();
  const [fileData, setFileData] = useState<fileInfoType>(DefaultNewFile);

  const { setTelemetryBaggage } = useTelemetry();

  const [activeTab, setActiveTab] = useState<string>(sessionStorage.getItem('ActiveTab') ?? '');
  const [tabValue, setTabValue] = useState<string>(sessionStorage.getItem('tabValue') ?? '0');

  const [openPendingCpl, setOpenPendingCpl] = useState<number>();
  const [partyUpdated, setPartyUpdated] = useState<boolean>(false);
  const [resetCpl, setResetCpl] = useState<boolean>(false);
  const [isRatesAndFeeDrawerClicked, setRatesAndFeeDrawerClicked] = useState<boolean>(false);
  const [fileDataUpdated, setfileDataUpdated] = useState<boolean>(false);
  const [hasRemittableCpl, setRemmittableCpl] = useState<boolean>();
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);

  // The Edit and Nav Bar use these for handling the unsaved-changes modal
  const [openSaveModal, setOpenSaveModal] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [openCreateDrawer, setOpenCreateDrawer] = useState<boolean>(false);
  const [openRatesAndFeeDrawer, setOpenRatesAndFeeDrawer] = useState<boolean>(false);
  const [isCreateDrawerClicked, setIsCreateDrawerClicked] = useState<boolean>(false);

  const [logoutAttempted, setLogoutAttempted] = useState<boolean>(false);
  const [cleanData, handleSetCleanData] = useState<fileInfoType | null | undefined>();
  const [firmAnchorEl, setFirmAnchorEl] = React.useState<null | HTMLElement>(null);

  const [officeDisabled, setOfficeDisabled] = useState(true);
  const [underwriterDisabled, setUnderwriterDisabled] = useState(true);
  const [resetFileNoDisable, setResetFileNoDisable] = useState(false);

  const [fileOrderInsightsData, setFileOrderInsightsData] = useState<FileOrderInsightsType | null>(null);
  const [isDiscoveringOrderInsights, setDiscoveringOrderInsights] = React.useState<string>('');
  const showFincenInOI = useFeatureFlag(FeatureFlag.SHOW_FINCEN_IN_ORDER_INSIGHTS, false);

  // Refresh trigger for file-related tabs
  const [fileRefreshTrigger, setFileRefreshTrigger] = useState(false);

  const triggerFileRefresh = useCallback((value: boolean) => {
    setFileRefreshTrigger(value);
  }, []);

  const [sessionFileId, setSessionFileIdState] = useState<string | null>(() => sessionStorage.getItem('fileId'));

  const setSessionFileId = useCallback((id: string | null) => {
    if (id) {
      sessionStorage.setItem('fileId', id);
    } else {
      sessionStorage.removeItem('fileId');
    }
    setSessionFileIdState(id);
  }, []);

  const setCleanData = (data: fileInfoType | null | undefined) => {
    /* It is very important to get a clean, deep clone here. */
    handleSetCleanData(_.cloneDeep(data));
  };

  const updateContext = async (fileInfo: fileInfoType | null) => {
    if (fileInfo) {
      /* format the closing date */
      fileInfo.closingDate = fileInfo.closingDate ? fileInfo.closingDate : null;

      setFileData(fileInfo);

      // Callers may pass an updater fn (CPL, jackets, rates-and-fee, parties); it has no fileId.
      if (typeof fileInfo !== 'function') setTelemetryBaggage(BaggageKeys.FileId, fileInfo.fileId);

      /* Do the clientside validation */
      const errs: FieldValidationError[] = await doValidate(fileInfo, fileSchema);
      setValidationErrors(errs);
    }
  };

  /** Use the fileId to get the file data */
  const loadFileData = async function (fileProps: GetFileInfoProps, callback?: () => void) {
    const token = await getAccessToken();
    const fileApiData = await getFileInfo(fileProps?.fileId ?? '', token);
    setCleanData(fileApiData);
    await updateContext(fileApiData);
    callback?.();
  };

  /* load filedata when lost on refresh */
  useEffect(() => {
    if (!fileData.fileId && sessionFileId) {
      loadFileData?.({ fileId: sessionFileId });
    }
  }, [fileData?.fileId, sessionFileId]);

  useEffect(() => {
    window.sessionStorage.setItem('ActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    window.sessionStorage.setItem('tabValue', tabValue);
  }, [tabValue]);

  //#region Order Insights
  //These functions work together to manage the process of retrieving
  //and updating order insights data for a file, handling both immediate
  //and delayed (polling) scenarios.

  //Fetches order insights data from an API using a file ID and token.
  const getOrderInsights = async function (fileId: string) {
    const token = await getAccessToken();
    return await getFileOrderInsights(fileId, token).catch(async () => {
      return OrderInsightsError;
    });
  };

  const handleOrderInsight = (data: FileOrderInsightsType) => {
    const modifiedData = {
      ...data,
      parties: data.parties.map((party: Party) => ({
        ...party,
        key: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${party.id}`,
      })),
    };
    setFileOrderInsightsData(modifiedData);
  };

  //Loads order insights data either immediately or by polling.
  const loadFileOrderInsightsData = async function (fileId: string, immediate = false) {
    setDiscoveringOrderInsights('pending');
    if (immediate) {
      const orderInsightsData = await getOrderInsights(fileId);
      handleOrderInsight(orderInsightsData);
      if (orderInsightsData?.isGenerating === true) {
        if (isDiscoveringOrderInsights !== 'pending') {
          pollFileOrderInsightsData(fileId);
        }
      } else {
        setDiscoveringOrderInsights('');
      }
    } else {
      pollFileOrderInsightsData(fileId);
    }
  };

  //Polls for order insights data at incremented intervals with max 60 sec until the data is ready.
  const pollFileOrderInsightsData = function (fileId: string) {
    let waitTime = 30000; // Start with 30 seconds
    const maxWaitTime = 60000; // Cap at 60 seconds
    const extendedPollingInterval = 30000; // 30 seconds for extended polling
    const maxExtendedDuration = 180000; // 3 minutes of extended polling
    let elapsedTime = 0; // Track total elapsed time

    const intervalId = setInterval(async () => {
      elapsedTime += waitTime;

      const orderInsightsData = await getOrderInsights(fileId);

      if (orderInsightsData?.isGenerating === false) {
        // Data is ready, update state and stop polling
        handleOrderInsight(orderInsightsData);

        triggerOrderInsightsNotification(orderInsightsData);
        setDiscoveringOrderInsights('');
        clearInterval(intervalId);
      } else if (elapsedTime > maxWaitTime + maxExtendedDuration) {
        // Handle timeout case show unable to load order insights
        handleOrderInsight({ ...orderInsightsData, hasError: true, isGenerating: false });
        // Stop polling after extended duration (3 minutes of 30s intervals)
        setDiscoveringOrderInsights('');
        clearInterval(intervalId);
      } else if (elapsedTime < maxWaitTime) {
        // Increment wait time by 10 seconds, up to the maxWaitTime
        waitTime = Math.min(waitTime + 10000, maxWaitTime);
      } else {
        // Switch to extended polling interval (30 seconds)
        waitTime = extendedPollingInterval;
      }
    }, waitTime);
  };

  const triggerOrderInsightsNotification = (orderInsightsData: FileOrderInsightsType) => {
    if (orderInsightsData?.isGenerating === false) {
      const allRisks = [
        ...extractRisks(orderInsightsData?.parties || [], ['Alert']),
        ...extractRisks(orderInsightsData?.properties || [], ['Alert']),
      ];
      const risks = showFincenInOI?.value ? allRisks : allRisks.filter((r) => r.categoryName !== 'FinCen');
      if (risks.length > 0) {
        addSnackbarMessage({
          title: `Potential Risk(s) Detected on File No: ${orderInsightsData?.fileNumber}`,
          html: true,
          message: 'View Order Insights to learn more.',
          type: 'warning',
          duration: 15000,
        });
      }
    }
  };

  //#endregion

  const ctx: FileDataContextInterface = {
    tabValue,
    setTabValue,
    activeTab,
    setActiveTab,
    openPendingCpl,
    setOpenPendingCpl,
    fileData,
    setFileData: updateContext,
    loadFileData,
    validationErrors,
    setValidationErrors,
    openSaveModal,
    setOpenSaveModal,
    isFormDirty,
    setIsFormDirty,
    cleanData,
    setCleanData,
    logoutAttempted,
    setLogoutAttempted,
    openCreateDrawer,
    setOpenCreateDrawer,
    isCreateDrawerClicked,
    setIsCreateDrawerClicked,
    setResetCpl,
    resetCpl,
    fileDataUpdated,
    setfileDataUpdated,
    hasRemittableCpl,
    setRemmittableCpl,
    partyUpdated,
    setPartyUpdated,
    firmAnchorEl,
    setFirmAnchorEl,
    officeDisabled,
    setOfficeDisabled,
    underwriterDisabled,
    setUnderwriterDisabled,
    resetFileNoDisable,
    setResetFileNoDisable,
    fileOrderInsightsData,
    loadFileOrderInsightsData,
    setFileOrderInsightsData,
    isDiscoveringOrderInsights,
    setRatesAndFeeDrawerClicked,
    isRatesAndFeeDrawerClicked,
    openRatesAndFeeDrawer,
    setOpenRatesAndFeeDrawer,
    fileRefreshTrigger,
    triggerFileRefresh,
    sessionFileId,
    setSessionFileId,
  };

  return <FileDataContext.Provider value={ctx}>{props.children}</FileDataContext.Provider>;
};

export { FileDataContext, FileDataProvider };
